import React from "react";
import FormSection from "../../../components/forms/FormSection";
import EditorCard, { AddRowButton } from "../../../components/forms/EditorCard";
import { Input } from "../../../components/ui/input";
import { MentionEditor } from "./mention-editor";
import { PromptMentionSource } from "./mention-editor/types";
import { AssemblyReferenceTemplateColumnWrite } from "../types/assembly-template";
import { PromptDataType } from "../types/prompt-model";

interface TemplateColumnsEditorProps {
  columns: AssemblyReferenceTemplateColumnWrite[];
  onChange: (columns: AssemblyReferenceTemplateColumnWrite[]) => void;
  /**
   * The prompts a column template can reference via "#". Each column pulls a
   * field of any selected prompt (documents as rows, prompts as columns).
   */
  prompts?: PromptMentionSource[];
  /**
   * Old single-prompt experience: each column uses the legacy "@" field mention
   * against the reference's one selected prompt instead of the "#" cascade.
   */
  singlePrompt?: boolean;
  /**
   * The single prompt's data type (single-prompt mode). When ListOfValues, the
   * legacy "@" picker's Answer option drills into the List As list options.
   */
  anchorPromptDataType?: PromptDataType;
}

/**
 * Editor for the per-column templates used when a "List As" reference renders
 * as a table. Each column has a name and its own rich-text mention template.
 * The supplied {@link TemplateColumnsEditorProps.prompts} are the prompts a
 * column can reference via "#" — the table builder (documents as rows, prompts
 * as columns).
 */
const TemplateColumnsEditor = ({
  columns,
  onChange,
  prompts,
  singlePrompt = false,
  anchorPromptDataType,
}: TemplateColumnsEditorProps) => {
  const patch = (index: number, change: Partial<AssemblyReferenceTemplateColumnWrite>) => {
    onChange(columns.map((col, i) => (i === index ? { ...col, ...change } : col)));
  };

  return (
    <div className="flex flex-col gap-2.5">
      {columns.map((column, index) => (
        <EditorCard
          key={index}
          title={`Column ${index + 1}`}
          removeLabel="Remove column"
          onRemove={() => onChange(columns.filter((_, i) => i !== index))}
        >
          <FormSection label="Heading">
            <Input
              placeholder="What this column is called in the table"
              value={column.name}
              onChange={(e) => patch(index, { name: e.target.value })}
            />
          </FormSection>
          <FormSection label="Cell contents">
            <MentionEditor
              value={column.template}
              onChange={(json) => patch(index, { template: json })}
              placeholder={
                singlePrompt
                  ? "Type @ to insert the answer, quote or document name…"
                  : "Type @ to insert a prompt, then . to pick its answer, document or quote…"
              }
              prompts={singlePrompt ? undefined : prompts}
              singlePrompt={singlePrompt}
              anchorPromptDataType={anchorPromptDataType}
            />
          </FormSection>
        </EditorCard>
      ))}
      <AddRowButton
        onClick={() => onChange([...columns, { name: "", template: { type: "doc", content: [] } }])}
      >
        Add column
      </AddRowButton>
    </div>
  );
};

export default TemplateColumnsEditor;
